@if (view(); as stepper) {
<nav class="order-stepper" [attr.aria-label]="StepperMessage.NavigationLabel">
  <div #bar class="stepper-bar flex align-items-center justify-content-start md:justify-content-center overflow-hidden white-space-nowrap">
    <ng-container *ngTemplateOutlet="barContent; context: { sections: shownSections(), more: hiddenCount(), grouped: grouped(), compact: compactDone(), stepper: stepper }"></ng-container>
  </div>

  <!-- Hidden copy with every section and the done pill, used only to measure what fits. -->
  <div #measure class="stepper-bar stepper-measure flex align-items-center white-space-nowrap" aria-hidden="true">
    <ng-container *ngTemplateOutlet="barContent; context: { sections: stepper.sections, more: 9, grouped: true, stepper: stepper, measuring: true }"></ng-container>
  </div>
</nav>

<ng-template #barContent let-sections="sections" let-more="more" let-grouped="grouped" let-compact="compact" let-stepper="stepper" let-measuring="measuring">
  <!-- Finished leading steps fold into the "N done" pill only when the full row does not fit. -->
  @if (grouped && stepper.groupedCount) {
  <button type="button" data-stepper-group class="stepper-done-pill flex align-items-center"
    [tabindex]="measuring ? -1 : 0" [disabled]="stepper.locked" (click)="openDialog()">
    <img src="assets/images/stepper/done-pill-check.svg" width="24" height="24" alt="">
    <span>{{ stepper.groupedCount }} {{ StepperMessage.Done }}</span>
    <img class="stepper-done-pill-chevron" src="assets/images/stepper/chevron-right-done.svg" width="16" height="16" alt="">
  </button>
  <img data-stepper-group src="assets/images/stepper/chevron-right-done.svg" width="16" height="16" alt="">
  }

  @for (section of sections; track section.id; let last = $last) {
  <span data-stepper-item class="stepper-entry flex align-items-center">
    <button type="button" class="stepper-item flex align-items-center"
      [class.is-completed]="section.status === StepperStatus.Completed"
      [class.is-in-progress]="section.status === StepperStatus.InProgress"
      [class.is-not-started]="section.status === StepperStatus.NotStarted"
      [class.is-current]="section.current"
      [tabindex]="measuring ? -1 : 0" [disabled]="stepper.locked"
      [attr.aria-current]="section.current ? 'step' : null" (click)="onSectionClick(section)">
      <ng-container *ngTemplateOutlet="stepIcon; context: { section: section }"></ng-container>
      <!-- Mobile shows only the current section's label. -->
      <span class="stepper-text flex-column align-items-center" [ngClass]="section.current ? 'flex' : 'hidden md:flex'">
        <span class="stepper-label" data-stepper-label>{{ section.label }}</span>
        <!-- A finished step drops its count first when the row is short of space. -->
        @if (section.total > 1 && !(compact && section.status === StepperStatus.Completed)) {
        <span class="stepper-count" [attr.data-stepper-done-count]="section.status === StepperStatus.Completed ? '' : null">{{ section.done }} {{ StepperMessage.Of }} {{ section.total }}</span>
        }
      </span>
    </button>
    @if (!last || more) {
    <img [src]="section.status === StepperStatus.Completed ? 'assets/images/stepper/chevron-right-done.svg' : 'assets/images/stepper/chevron-right.svg'"
      width="16" height="16" alt="">
    }
  </span>
  }

  @if (more) {
  <button type="button" data-stepper-more class="stepper-more-pill" [tabindex]="measuring ? -1 : 0" (click)="openDialog()">
    +{{ more }} {{ StepperMessage.More }}
  </button>
  }

  @if (stepper.readyToFinalize) {
  <span data-stepper-fixed class="stepper-ready flex align-items-center">
    <img src="assets/images/stepper/ready.svg" width="16" height="16" alt="">
    <span class="hidden md:inline">{{ StepperMessage.ReadyToFinalize }}</span>
    <span class="md:hidden">{{ StepperMessage.Ready }}</span>
  </span>
  }
</ng-template>

<!-- 28px step circle: green check when done, progress ring for a step with several pages, number otherwise. -->
<ng-template #stepIcon let-section="section">
  @if (section.status === StepperStatus.Completed) {
  <img class="stepper-icon" src="assets/images/stepper/step-complete.svg" width="28" height="28" alt="">
  } @else if (section.total > 1 && section.status === StepperStatus.InProgress) {
  <svg class="stepper-icon stepper-ring" viewBox="0 0 28 28" width="28" height="28" aria-hidden="true">
    <circle class="stepper-ring-fill" cx="14" cy="14" r="14"></circle>
    @for (segment of section.ring; track $index) {
    <circle cx="14" cy="14" [attr.r]="ringRadius" fill="none" stroke-width="4"
      [class.is-completed]="segment.completed"
      [attr.stroke-dasharray]="segment.dashArray" [attr.stroke-dashoffset]="segment.dashOffset"
      transform="rotate(-90 14 14)"></circle>
    }
    <text x="14" y="19" text-anchor="middle">{{ section.number }}</text>
  </svg>
  } @else {
  <span class="stepper-icon stepper-number flex align-items-center justify-content-center"
    [class.is-current]="section.current">{{ section.number }}</span>
  }
</ng-template>

<p-dialog [visible]="dialogVisible()" (visibleChange)="dialogVisible.set($event)" [modal]="true" [draggable]="false"
  [resizable]="false" [dismissableMask]="true" [showHeader]="false" [style]="{ width: '420px', maxWidth: '95vw' }"
  styleClass="order-stepper-dialog" maskStyleClass="order-stepper-mask" appendTo="body">
  <div class="stepper-dialog-heading flex align-items-center justify-content-between">
    <span class="stepper-dialog-title">
      {{ StepperMessage.OrderProgress }}
      <span class="stepper-dialog-count">({{ stepper.completedCount }} {{ StepperMessage.Of }} {{ stepper.sections.length }} {{ StepperMessage.CompleteSuffix }})</span>
    </span>
    <button type="button" class="stepper-dialog-close flex align-items-center justify-content-center" (click)="dialogVisible.set(false)">
      <img src="assets/images/stepper/dialog-close.svg" width="16" height="16" alt="">
    </button>
  </div>

  <div class="stepper-dialog-steps flex flex-column">
    @for (section of stepper.sections; track section.id) {
    <div class="stepper-card"
      [class.is-completed]="section.status === StepperStatus.Completed"
      [class.is-in-progress]="section.status === StepperStatus.InProgress"
      [class.is-not-started]="section.status === StepperStatus.NotStarted"
      [class.is-open]="isExpanded(section.id)">
      <button type="button" class="stepper-card-heading flex align-items-center w-full text-left"
        [disabled]="isSectionDisabled(section)" [attr.aria-expanded]="section.total > 1 ? isExpanded(section.id) : null"
        (click)="onDialogSectionClick(section)">
        <ng-container *ngTemplateOutlet="dialogIcon; context: { section: section }"></ng-container>
        <span class="flex flex-column flex-1">
          <span class="stepper-card-title">{{ section.label }}</span>
          <span class="stepper-card-status">
            @switch (section.status) {
            @case (StepperStatus.Completed) { {{ StepperMessage.Complete }} }
            @case (StepperStatus.InProgress) { {{ StepperMessage.InProgress }} }
            @default { {{ StepperMessage.NotStarted }} }
            }
            @if (section.total > 1) { &middot; {{ section.done }} {{ StepperMessage.Of }} {{ section.total }} }
          </span>
        </span>
        @if (section.total > 1) {
        <img class="stepper-disclosure" [class.is-up]="isExpanded(section.id)" src="assets/images/stepper/dialog-disclosure.svg"
          width="12.5" height="7.25" alt="">
        } @else {
        <img class="stepper-go" src="assets/images/stepper/dialog-step-go.svg" width="20" height="20" alt="">
        }
      </button>

      @if (section.total > 1 && isExpanded(section.id)) {
      <div class="stepper-substeps flex flex-column">
        @for (child of section.children; track child.route; let i = $index) {
        <button type="button" class="stepper-substep flex align-items-center w-full text-left"
          [class.is-completed]="child.status === StepperStatus.Completed"
          [class.is-in-progress]="child.status === StepperStatus.InProgress"
          [class.is-not-started]="child.status === StepperStatus.NotStarted"
          [disabled]="child.status === StepperStatus.NotStarted || stepper.locked"
          [attr.aria-current]="child.current ? 'step' : null" (click)="goTo(child)">
          @if (child.status === StepperStatus.Completed) {
          <img src="assets/images/stepper/dialog-substep-complete.svg" width="24" height="24" alt="">
          } @else {
          <span class="stepper-substep-number flex align-items-center justify-content-center">{{ i + 1 }}</span>
          }
          <span class="flex flex-column flex-1">
            <span class="stepper-substep-title">{{ child.title }}</span>
            <span class="stepper-card-status">
              @switch (child.status) {
              @case (StepperStatus.Completed) { {{ StepperMessage.Complete }} }
              @case (StepperStatus.InProgress) { {{ StepperMessage.InProgress }} }
              @default { {{ StepperMessage.NotStarted }} }
              }
            </span>
          </span>
          <img class="stepper-go" src="assets/images/stepper/dialog-step-go.svg" width="20" height="20" alt="">
        </button>
        }
      </div>
      }
    </div>
    }
  </div>
</p-dialog>

<ng-template #dialogIcon let-section="section">
  @if (section.status === StepperStatus.Completed) {
  <img src="assets/images/stepper/dialog-step-complete.svg" width="28" height="28" alt="">
  } @else {
  <ng-container *ngTemplateOutlet="stepIcon; context: { section: section }"></ng-container>
  }
</ng-template>
}
